import React from 'react';

// Glass Card Component
export const GlassCard = ({ children, className = "", hover = true, ...props }) => (
  <div 
    className={`
      bg-white/80 dark:bg-gray-800/80 
      backdrop-blur-lg 
      border border-white/20 dark:border-gray-700/20
      rounded-xl shadow-glass
      ${hover ? 'hover:shadow-glow hover:scale-[1.02] transition-all duration-300' : ''}
      animate-fade-in
      ${className}
    `}
    {...props}
  >
    {children}
  </div>
);

// Glass Button Component
export const GlassButton = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  className = "", 
  disabled = false,
  ...props 
}) => {
  const baseClasses = `
    relative overflow-hidden
    backdrop-blur-lg
    border border-white/20 dark:border-gray-700/20
    rounded-lg font-medium
    transition-all duration-300
    hover:scale-105 active:scale-95
    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
    animate-scale-in
  `;

  const variants = {
    primary: `
      bg-gradient-to-r from-primary-500/90 to-primary-600/90
      hover:from-primary-600/90 hover:to-primary-700/90
      text-white shadow-glow-primary
      hover:shadow-glow-primary
    `,
    secondary: `
      bg-white/10 dark:bg-gray-800/10
      hover:bg-white/20 dark:hover:bg-gray-700/20
      text-gray-700 dark:text-gray-200
      shadow-glass
    `,
    success: `
      bg-gradient-to-r from-success-500/90 to-success-600/90
      hover:from-success-600/90 hover:to-success-700/90
      text-white shadow-glow
    `,
    danger: `
      bg-gradient-to-r from-danger-500/90 to-danger-600/90
      hover:from-danger-600/90 hover:to-danger-700/90
      text-white shadow-glow
    `,
  };

  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
    xl: 'px-8 py-4 text-xl',
  };

  return (
    <button
      className={`
        ${baseClasses}
        ${variants[variant]}
        ${sizes[size]}
        ${className}
      `}
      disabled={disabled}
      {...props}
    >
      <span className="relative z-10">{children}</span>
      {/* Shimmer effect */}
      <div className="absolute inset-0 -top-2 -bottom-2 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 translate-x-[-200%] hover:translate-x-[200%] transition-transform duration-700" />
    </button>
  );
};

// Glass Input Component
export const GlassInput = ({ className = "", error = false, ...props }) => (
  <input
    className={`
      w-full px-4 py-3
      bg-white/50 dark:bg-gray-800/50
      backdrop-blur-lg
      border border-white/30 dark:border-gray-700/30
      ${error ? 'border-danger-500/50' : 'focus:border-primary-500/50'}
      rounded-lg
      text-gray-900 dark:text-gray-100
      placeholder-gray-500 dark:placeholder-gray-400
      focus:outline-none focus:ring-2 focus:ring-primary-500/20
      transition-all duration-300
      hover:bg-white/60 dark:hover:bg-gray-800/60
      ${className}
    `}
    {...props}
  />
);

// Glass Select Component
export const GlassSelect = ({ children, className = "", error = false, ...props }) => (
  <select
    className={`
      w-full px-4 py-3
      bg-white/50 dark:bg-gray-800/50
      backdrop-blur-lg
      border border-white/30 dark:border-gray-700/30
      ${error ? 'border-danger-500/50' : 'focus:border-primary-500/50'}
      rounded-lg
      text-gray-900 dark:text-gray-100
      focus:outline-none focus:ring-2 focus:ring-primary-500/20
      transition-all duration-300
      hover:bg-white/60 dark:hover:bg-gray-800/60
      ${className}
    `}
    {...props}
  >
    {children}
  </select>
);

// Glass Modal Component
export const GlassModal = ({ children, isOpen, onClose, className = "" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className={`
        relative z-10
        bg-white/90 dark:bg-gray-800/90
        backdrop-blur-xl
        border border-white/20 dark:border-gray-700/20
        rounded-2xl shadow-glass
        animate-scale-in
        max-w-md w-full max-h-[90vh] overflow-y-auto
        ${className}
      `}>
        {children}
      </div>
    </div>
  );
};

// Glass Badge Component
export const GlassBadge = ({ children, variant = 'primary', className = "" }) => {
  const variants = {
    primary: 'bg-primary-500/20 text-primary-700 dark:text-primary-300 border-primary-500/30',
    success: 'bg-success-500/20 text-success-700 dark:text-success-300 border-success-500/30',
    warning: 'bg-warning-500/20 text-warning-700 dark:text-warning-300 border-warning-500/30',
    danger: 'bg-danger-500/20 text-danger-700 dark:text-danger-300 border-danger-500/30',
    secondary: 'bg-gray-500/20 text-gray-700 dark:text-gray-300 border-gray-500/30',
  };

  return (
    <span className={`
      inline-flex items-center px-2.5 py-0.5
      backdrop-blur-lg
      border rounded-full
      text-xs font-medium
      ${variants[variant]}
      ${className}
    `}>
      {children}
    </span>
  );
};

// Glass Navigation Item
export const GlassNavItem = ({ children, active = false, className = "", ...props }) => (
  <div
    className={`
      px-4 py-2 rounded-lg
      backdrop-blur-lg
      border border-transparent
      transition-all duration-300
      cursor-pointer
      ${active 
        ? 'bg-primary-500/20 border-primary-500/30 text-primary-700 dark:text-primary-300' 
        : 'hover:bg-white/10 dark:hover:bg-gray-700/10 hover:border-white/20 dark:hover:border-gray-700/20'
      }
      ${className}
    `}
    {...props}
  >
    {children}
  </div>
);
